import { Box, Button, Divider, FormField, Image, Legend, Paragraph, Text, Textarea } from '@zillow/constellation-10';
// oxlint-disable-next-line constellation/no-styled-factory -- pre-existing styled-components-style usage, not migrating in a lint opt-in
import { styled } from '@/styled-system/jsx';
import type { MetaAdsProps } from './BoostWidget.types';
import { BuildingMediaMessage } from './BuildingMediaMessage';
import { ListingDescriptionModal } from './ListingDescriptionModal';
import mediaImage from '../../images/media-post.png';
import { FairHousingAlert } from './FairHousingAlert';
import { MetaAdsPreview } from '../MetaAdsPreview/MetaAdsPreview';

// oxlint-disable-next-line constellation/no-styled-factory -- pre-existing styled-components-style usage, not migrating in a lint opt-in
const ImageGallery = styled('div', {
  base: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 'md',
  },
});

// oxlint-disable-next-line constellation/no-styled-factory -- pre-existing styled-components-style usage, not migrating in a lint opt-in
const GalleryImageContainer = styled('div', {
  base: {
    position: 'relative',
    paddingTop: '60%',
    overflow: 'hidden',
    borderRadius: '4px',
    '& img': {
      top: 0,
      left: 0,
      position: 'absolute',
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  },
});

// oxlint-disable-next-line constellation/no-styled-factory -- pre-existing styled-components-style usage, not migrating in a lint opt-in
const NumberBadge = styled(Text, {
  base: {
    position: 'absolute',
    bottom: '400',
    left: '400',
    borderRadius: '48px',
    width: '23px',
    height: '20px',
    color: 'white',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
});

// oxlint-disable-next-line constellation/no-styled-factory -- pre-existing styled-components-style usage, not migrating in a lint opt-in
const NoMetaAdsContainer = styled(Box, {
  base: {
    // oxlint-disable-next-line constellation/prefer-semantic-tokens -- in the zillow light block (which the consuming apps render) every same-value semantic token is an action state; bg.feedback.info.soft is #dddde1 there, not blue.100's #e2f0ff
    backgroundColor: 'blue.100',
    padding: 'xl',
    margin: '-md',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexDirection: { base: 'column', lg: 'row' },
    '& img': {
      maxWidth: '390px',
    },
    '& .left-content': {
      maxWidth: '540px',
    },
  },
});

const GalleryImage = ({ src, index }: { src: string; index: number }) => (
  <GalleryImageContainer>
    <Image src={src} />
    <NumberBadge textStyle="body-sm-bold">{index}</NumberBadge>
  </GalleryImageContainer>
);

export const MetaAds = ({
  previewImages,
  disabled,
  formValues,
  dirtyFields,
  errors,
  onChangeFormValue,
  displayMediaMessage,
  shouldValidateMetaAds,
  title,
  address,
  description,
  onClickSeeBuildingMedia,
  displayFairHousingError,
  displayListingDescriptionModal,
}: MetaAdsProps) => {
  const imagesToDisplay = previewImages.slice(0, 4);

  if (!shouldValidateMetaAds)
    return (
      <NoMetaAdsContainer>
        <Box className="left-content">
          <Paragraph css={{ marginBottom: 'md', fontWeight: 'bold', fontSize: 'lg' }}>
            Meta ads are not included in this package
          </Paragraph>
          <Paragraph css={{ marginBottom: 'lg' }}>
            Upgrade to a Premium or Signature package to get targeted Facebook and Instagram ads. These will appear in
            the feeds and Stories of renters who are looking for rentals in your area.
          </Paragraph>
          <Box css={{ display: 'flex', gap: 'sm' }}>
            <Button tone="neutral" emphasis="outlined" asChild>
              {/* oxlint-disable-next-line next/no-html-link-for-pages -- not a Next.js app, this module has no next/link available */}
              <a href="/rental-manager/ad-management/self-serve-data/sales-pro">Upgrade package</a>
            </Button>

            <MetaAdsPreview
              previewImages={imagesToDisplay}
              address={address}
              title={title}
              description={formValues.adCopy || description}
              trigger={
                <Button tone="neutral" emphasis="outlined">
                  Preview sample ad
                </Button>
              }
            />
          </Box>
        </Box>
        <Image src={mediaImage} />
      </NoMetaAdsContainer>
    );

  return (
    <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', paddingInline: 'sm', paddingBlock: 'xl' }}>
      <Box css={{ gridColumn: { base: 'span 2', lg: 'span 1' }, paddingRight: { lg: 'md' } }}>
        {displayFairHousingError && <FairHousingAlert />}
        <Paragraph css={{ marginBottom: 'sm' }}>
          Included in Meta ads: 1 Facebook ad, 1 Instagram story, 1 Instagram post
        </Paragraph>
        <FormField
          label={<Legend>Facebook/Instagram ad copy</Legend>}
          description={
            <Box>
              {dirtyFields.get('adCopy') && errors.get('adCopy') && (
                <Text
                  textStyle="body-xs"
                  css={{
                    color:
                      'red.600' /* oxlint-disable-line constellation/prefer-semantic-tokens -- ambiguous semantic-token suggestion, not migrating in a lint opt-in */,
                  }}
                >
                  {errors.get('adCopy')}
                </Text>
              )}
              <Text textStyle="body-xs">225 characters allowed</Text>
            </Box>
          }
          error={dirtyFields.get('adCopy') && Boolean(errors.get('adCopy'))}
          required
        >
          <Textarea
            value={formValues.adCopy}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChangeFormValue('adCopy', e.target.value)}
            disabled={disabled}
          />
        </FormField>
        <Box css={{ marginTop: 'md' }} />
        <Box css={{ display: 'flex', justifyContent: 'space-between', marginTop: 'lg', marginBottom: 'xl' }}>
          <MetaAdsPreview
            previewImages={imagesToDisplay}
            address={address}
            title={title}
            description={formValues.adCopy || description}
            trigger={<Button>Preview ads</Button>}
          />
          {displayListingDescriptionModal && (
            <ListingDescriptionModal title={title} address={address} description={description} />
          )}
        </Box>
        <Divider css={{ marginBlock: 'lg' }} />
        <Paragraph textStyle="body-sm-bold" css={{ marginBottom: 'md' }}>
          Not sure what to write? Here&apos;s an example.{' '}
        </Paragraph>
        <Paragraph>
          Residents at Olympic Apartments, located in beautiful Seattle, WA can select from spacious 1, 2, or 3-bedroom
          units, all with updated finishes. Both mountain and water views available. Check out all our must-see
          amenities!
        </Paragraph>
      </Box>
      <Box css={{ gridColumn: { base: 'span 2', lg: 'span 1' }, paddingRight: { lg: 'md' } }}>
        <Text textStyle="body-sm-bold" css={{ marginBottom: 'sm', display: 'block' }}>
          Facebook and Instagram ad images
        </Text>
        <ImageGallery>
          {imagesToDisplay.map((src, index) => (
            <GalleryImage key={src} src={src} index={index + 1} />
          ))}
        </ImageGallery>
        {displayMediaMessage && (
          <>
            <Box css={{ marginTop: 'lg' }} />
            <BuildingMediaMessage onClickSeeBuildingMedia={onClickSeeBuildingMedia} />
          </>
        )}
      </Box>
    </Box>
  );
};
